import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers, noString } from "./shared";
import { Patient } from "../patient.model";

interface IntakeOutputFields {
  lineDescription: string;
  lineIntake: string;
  lineOutput: string;

  tubeDescription: string;
  tubeIntake: string;
  tubeOutput: string;

  airwayDescription: string;

  drainDescription: string;
  drainIntake: string;
  drainOutput: string;

  oralDescription: string;
  oralIntake: string;
  oralEmesis: string;

  stoolDescription: string;
  stoolOutput: string;



  infantNutrition: boolean;

  breastfeeding: boolean;
  feedingOccurrence: string;

  // Latch scale
  LSlatch: string;
  LSaudible: string;
  LStype: string;
  LScomfort: string;
  LShold: string;
  LSscore: number;

  bottlefeeding: boolean;
  feedingTolerance: string;
  formula: string;
  numOfOunces: string;
}
export type IntakeOutputFormGroup = FormGroup<ControlsOf<IntakeOutputFields>>;

export const IntakeOutputAssessmentHelpers: AssessmentHelpers<IntakeOutputAssessment, Patient> = {
  createFormGroup: createIntakeOutputFormGroup,
  toPDF: intakeOutputAssessmentToPDF,
  formToObject: formToIntakeOutputAssessment,
  initialState: {
    lineDescription: '',
    lineIntake: '',
    lineOutput: '',

    tubeDescription: '',
    tubeIntake: '',
    tubeOutput: '',

    airwayDescription: '',

    drainDescription: '',
    drainIntake: '',
    drainOutput: '',

    oralDescription: '',
    oralIntake: '',
    oralEmesis: '',

    stoolDescription: '',
    stoolOutput: '',

    infantNutrition: false,

    breastfeeding: false,
    feedingOccurrence: '',

    // Latch scale
    LSlatch: '',
    LSaudible: '',
    LStype: '',
    LScomfort: '',
    LShold: '',
    LSscore: 0,

    bottlefeeding: false,
    feedingTolerance: '',
    formula: '',
    numOfOunces: '',
  },
};

/**
 * Holds information about the patient's intake and output assessment.
 */
export class IntakeOutputAssessment {
  lineDescription: string;
  lineIntake: string;
  lineOutput: string;

  tubeDescription: string;
  tubeIntake: string;
  tubeOutput: string;

  airwayDescription: string;

  drainDescription: string;
  drainIntake: string;
  drainOutput: string;

  oralDescription: string;
  oralIntake: string;
  oralEmesis: string;

  stoolDescription: string;
  stoolOutput: string;

  infantNutrition: boolean;

  breastfeeding: boolean;
  feedingOccurrence: string;

  // Latch scale
  LSlatch: string;
  LSaudible: string;
  LStype: string;
  LScomfort: string;
  LShold: string;
  LSscore: number;

  bottlefeeding: boolean;
  feedingTolerance: string;
  formula: string;
  numOfOunces: string;

  constructor(data: Partial<IntakeOutputFields>) {
    this.lineDescription = data.lineDescription ?? IntakeOutputAssessmentHelpers.initialState['lineDescription'];
    this.lineIntake = data.lineIntake ?? IntakeOutputAssessmentHelpers.initialState['lineIntake'];
    this.lineOutput = data.lineOutput ?? IntakeOutputAssessmentHelpers.initialState['lineOutput'];

    this.tubeDescription = data.tubeDescription ?? IntakeOutputAssessmentHelpers.initialState['tubeDescription'];
    this.tubeIntake = data.tubeIntake ?? IntakeOutputAssessmentHelpers.initialState['tubeIntake'];
    this.tubeOutput = data.tubeOutput ?? IntakeOutputAssessmentHelpers.initialState['tubeOutput'];

    this.airwayDescription = data.airwayDescription ?? IntakeOutputAssessmentHelpers.initialState['airwayDescription'];

    this.drainDescription = data.drainDescription ?? IntakeOutputAssessmentHelpers.initialState['drainDescription'];
    this.drainIntake = data.drainIntake ?? IntakeOutputAssessmentHelpers.initialState['drainIntake'];
    this.drainOutput = data.drainOutput ?? IntakeOutputAssessmentHelpers.initialState['drainOutput'];

    this.oralDescription = data.oralDescription ?? IntakeOutputAssessmentHelpers.initialState['oralDescription'];
    this.oralIntake = data.oralIntake ?? IntakeOutputAssessmentHelpers.initialState['oralIntake'];
    this.oralEmesis = data.oralEmesis ?? IntakeOutputAssessmentHelpers.initialState['oralEmesis'];

    this.stoolDescription = data.stoolDescription ?? IntakeOutputAssessmentHelpers.initialState['stoolDescription'];
    this.stoolOutput = data.stoolOutput ?? IntakeOutputAssessmentHelpers.initialState['stoolOutput'];

    this.infantNutrition = data.infantNutrition ?? IntakeOutputAssessmentHelpers.initialState['infantNutrition'];

    this.breastfeeding = data.breastfeeding ?? IntakeOutputAssessmentHelpers.initialState['breastfeeding'];
    this.feedingOccurrence = data.feedingOccurrence ?? IntakeOutputAssessmentHelpers.initialState['feedingOccurrence'];

    this.LSlatch = data.LSlatch ?? IntakeOutputAssessmentHelpers.initialState['LSlatch'];
    this.LSaudible = data.LSaudible ?? IntakeOutputAssessmentHelpers.initialState['LSaudible'];
    this.LStype = data.LStype ?? IntakeOutputAssessmentHelpers.initialState['LStype'];
    this.LScomfort = data.LScomfort ?? IntakeOutputAssessmentHelpers.initialState['LScomfort'];
    this.LShold = data.LShold ?? IntakeOutputAssessmentHelpers.initialState['LShold'];
    this.LSscore = data.LSscore ?? IntakeOutputAssessmentHelpers.initialState['LSscore'];

    this.bottlefeeding = data.bottlefeeding ?? IntakeOutputAssessmentHelpers.initialState['bottlefeeding'];
    this.feedingTolerance = data.feedingTolerance ?? IntakeOutputAssessmentHelpers.initialState['feedingTolerance'];
    this.formula = data.formula ?? IntakeOutputAssessmentHelpers.initialState['formula'];
    this.numOfOunces = data.numOfOunces ?? IntakeOutputAssessmentHelpers.initialState['numOfOunces'];
  }
}

function createIntakeOutputFormGroup(intakeOutputAssessment?: IntakeOutputAssessment) {
  const data = intakeOutputAssessment || new IntakeOutputAssessment(IntakeOutputAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<IntakeOutputFields>>({
    lineDescription: new FormControl<string | null>(data.lineDescription),
    lineIntake: new FormControl<string | null>(data.lineIntake),
    lineOutput: new FormControl<string | null>(data.lineOutput),
    tubeDescription: new FormControl<string | null>(data.tubeDescription),
    tubeIntake: new FormControl<string | null>(data.tubeIntake),
    tubeOutput: new FormControl<string | null>(data.tubeOutput),
    airwayDescription: new FormControl<string | null>(data.airwayDescription),
    drainDescription: new FormControl<string | null>(data.drainDescription),
    drainIntake: new FormControl<string | null>(data.drainIntake),
    drainOutput: new FormControl<string | null>(data.drainOutput),
    oralDescription: new FormControl<string | null>(data.oralDescription),
    oralIntake: new FormControl<string | null>(data.oralIntake),
    oralEmesis: new FormControl<string | null>(data.oralEmesis),
    stoolDescription: new FormControl<string | null>(data.stoolDescription),
    stoolOutput: new FormControl<string | null>(data.stoolOutput),
    infantNutrition: new FormControl<boolean | null>(data.infantNutrition),
    breastfeeding: new FormControl<boolean | null>(data.breastfeeding),
    feedingOccurrence: new FormControl<string | null>(data.feedingOccurrence),
    LSlatch: new FormControl<string | null>(data.LSlatch),
    LSaudible: new FormControl<string | null>(data.LSaudible),
    LStype: new FormControl<string | null>(data.LStype),
    LScomfort: new FormControl<string | null>(data.LScomfort),
    LShold: new FormControl<string | null>(data.LShold),
    LSscore: new FormControl<number | null>(data.LSscore),
    bottlefeeding: new FormControl<boolean | null>(data.bottlefeeding),
    feedingTolerance: new FormControl<string | null>(data.feedingTolerance),
    formula: new FormControl<string | null>(data.formula),
    numOfOunces: new FormControl<string | null>(data.numOfOunces),
  });
}

function intakeOutputAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: IntakeOutputAssessment, patient: Patient): void {
  pdf.addSectionTitle("Intake and Output");
  pdf.pdf.setFont("Helvetica", "normal");
  pdf.pdf.text("Line", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.lineDescription)
  pdf.addTwoColumnText("Intake", assessment.lineIntake, "Output", assessment.lineOutput);
  pdf.currY += 10;
  pdf.pdf.text("Tube", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.tubeDescription)
  pdf.addTwoColumnText("Intake", assessment.tubeIntake, "Output", assessment.tubeOutput);
  pdf.currY += 10;
  pdf.pdf.text("Airway", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.airwayDescription)
  pdf.currY += 10;
  pdf.pdf.text("Drain", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.drainDescription)
  pdf.addTwoColumnText("Intake", assessment.drainIntake, "Output", assessment.drainOutput);
  pdf.currY += 10;
  pdf.pdf.text("Oral", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.oralDescription)
  pdf.addTwoColumnText("Intake", assessment.oralIntake, "Emesis", assessment.oralEmesis);
  pdf.currY += 10;
  pdf.pdf.text("Stool", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.stoolDescription);
  pdf.addOneColumnText("Output", assessment.stoolOutput);
  pdf.currY += 10;
  if (patient.isPediatric()) {
    pdf.currY += 20;
    pdf.addOneColumnText("Infant Nutrition", assessment.infantNutrition ? "Yes" : "No");

    if (assessment.infantNutrition) {
      pdf.addOneColumnText("Breastfeeding", assessment.breastfeeding ? "Yes" : "No");

      if (assessment.breastfeeding) {
        pdf.addOneColumnText("Feeding Occurrence", assessment.feedingOccurrence);
        pdf.currY += 10;
        pdf.pdf.text("Latch Scale", pdf.margin, pdf.currY);
        pdf.currY += 20;
        pdf.addOneColumnText("Latch", assessment.LSlatch);
        pdf.addOneColumnText("Audible", assessment.LSaudible);
        pdf.addOneColumnText("Type", assessment.LStype);
        pdf.addOneColumnText("Comfort", assessment.LScomfort);
        pdf.addOneColumnText("Hold", assessment.LShold);
        pdf.addOneColumnText("Score", assessment.LSscore.toString());
        pdf.currY += 10;
      }

      pdf.addOneColumnText("Bottlefeeding", assessment.bottlefeeding ? "Yes" : "No");

      if (assessment.bottlefeeding) {
        pdf.addOneColumnText("Feeding Tolerance", assessment.feedingTolerance);
        pdf.addOneColumnText("Formula", assessment.formula);
        pdf.addOneColumnText("Number of Ounces", assessment.numOfOunces);
        pdf.currY += 10;
      }
    }
  }
  pdf.currY += pdf.newSection;
}

function formToIntakeOutputAssessment(fg: IntakeOutputFormGroup) {
  return new IntakeOutputAssessment({
    lineDescription: fg.controls.lineDescription.value!,
    lineIntake: fg.controls.lineIntake.value!,
    lineOutput: fg.controls.lineOutput.value!,

    tubeDescription: fg.controls.tubeDescription.value!,
    tubeIntake: fg.controls.tubeIntake.value!,
    tubeOutput: fg.controls.tubeOutput.value!,

    airwayDescription: fg.controls.airwayDescription.value!,

    drainDescription: fg.controls.drainDescription.value!,
    drainIntake: fg.controls.drainIntake.value!,
    drainOutput: fg.controls.drainOutput.value!,

    oralDescription: fg.controls.oralDescription.value!,
    oralIntake: fg.controls.oralIntake.value!,
    oralEmesis: fg.controls.oralEmesis.value!,

    stoolDescription: fg.controls.stoolDescription.value!,
    stoolOutput: fg.controls.stoolOutput.value!,

    infantNutrition: fg.controls.infantNutrition.value!,

    breastfeeding: fg.controls.breastfeeding.value!,
    feedingOccurrence: fg.controls.feedingOccurrence.value!,

    LSlatch: fg.controls.LSlatch.value!,
    LSaudible: fg.controls.LSaudible.value!,
    LStype: fg.controls.LStype.value!,
    LScomfort: fg.controls.LScomfort.value!,
    LShold: fg.controls.LShold.value!,
    LSscore: fg.controls.LSscore.value!,

    bottlefeeding: fg.controls.bottlefeeding.value!,
    feedingTolerance: fg.controls.feedingTolerance.value!,
    formula: fg.controls.formula.value!,
    numOfOunces: fg.controls.numOfOunces.value!,
  });
}
